/**
 * The theatre's picture: the movement's still, or its demo film when the catalogue names one.
 *
 * ── A `<video>` where there is a film, an `<img>` where there is not ────────────────────────
 * `videoUrl` is on the catalogue's own row, so which of the two this is is known on the first
 * frame and the box never swaps under the trainer. The poster is the SAME still the `<img>` would
 * have drawn, so the two states are one picture with and without a control strip: nothing moves
 * when a film happens to be there.
 *
 * The browser's own `controls` rather than a hand-built player, because they are keyboard-operable,
 * announced and already translated — but only ONCE THE FILM IS RUNNING. The canon draws a clean
 * picture with a play disc and no strip across the bottom, and a `<video controls>` showing its
 * poster paints that strip over exactly where the disc stands. So the
 * canon's play button is the only control until it is pressed, and it is a real one: it calls
 * `play()` on the element beside it and hands the rest to the browser.
 *
 * ── The box is FIXED and the picture is cropped into it ─────────────────────────────────────
 * `flex: 1` over `--h-ex-theatre-hero`, which is the specimen's own construction: the picture takes
 * whatever the similar strip under it leaves, and `object-fit: cover` frames it. A hard 16:9 would
 * leave a band of dead panel under a 1240px card. Either way the height is settled by the panel and
 * not by the file, so a slow photograph changes ink and never geometry.
 *
 * ── No time chip ────────────────────────────────────────────────────────────────────────────
 * The specimen draws `0:18` in the top right. Duration is not on the wire — `GET /exercise` carries
 * none — and a figure this app would have to measure from the file after it loads is a number that
 * arrives late and pushes nothing but is still a claim we cannot make. Deviation 2 in the canon's
 * own register.
 *
 * ── What became of the files a save sent is DRAWN here ──────────────────────────────────────
 * `mediaNote` — a cover that did not go up, a film being processed or not added — stands where
 * „Brak okładki” sits when there is no picture (D23), and on a plate in the box's top corner when
 * there is one: a film can be processing under a cover that went up fine. Either way it is inside
 * this fixed box, so its arrival and its departure move nothing on the card.
 *
 * ── The plates are theme-INDEPENDENT, and their tokens say so ───────────────────────────────
 * The play disc and the media note sit on a photograph, which is the same picture in Flame and in
 * Flame Dark, so a token that flipped with the theme would put dark ink on a dark photograph.
 * `--plate-on-photo` and `--text-on-plate` in `tokens/colors.css` are where those values live.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BarbellGlyph } from '~/shared/ui/ExerciseGlyphs';
import photoBox from '~/shared/ui/photoBox.module.css';

import type { ExerciseRow } from '../model/exerciseRow';

import { PlayGlyph } from './ExerciseLibraryIcons';
import styles from './TheatreHero.module.css';

interface TheatreHeroProps {
  readonly row: ExerciseRow;
  /**
   * The picture to draw, and NOT `row.stillUrl`.
   *
   * The row's still leads with the poster because the table's 44px thumb cares about coverage; this
   * box is 636 css px at the reference desk and cares about resolution, so it leads with the cover.
   * The two orders are
   * `heroStillUrl` and `stillUrl` in `entities/exercise-library`, which is the only module that
   * knows both fields — the theatre passes the one it wants rather than this file choosing.
   */
  readonly stillUrl: string | null;
  /** The demo film, or `null`. The catalogue answers it; nothing here waits for it. */
  readonly videoUrl: string | null;
  /**
   * What became of the files the last save sent — a failed cover, a film being processed or not
   * added — so it is SEEN and not only spoken. `null` otherwise.
   */
  readonly mediaNote: string | null;
  /**
   * The tab panel this box is when the source tabs above it are drawn (`TheatreSources`), so their
   * `aria-controls` lands here. Absent when the movement has no film to switch to.
   */
  readonly panel?: { readonly id: string; readonly tabId: string };
}

export function TheatreHero({ row, stillUrl, videoUrl, mediaNote, panel }: TheatreHeroProps) {
  const { t } = useTranslation();
  const film = useRef<HTMLVideoElement>(null);
  // The browser's control strip, from the moment the trainer asks for it. See the header.
  const [playing, setPlaying] = useState(false);
  // A still the catalogue NAMES and the media root does not hold draws Chrome's broken-file mark,
  // which is worse than the tile it replaces. Ten of the named stills 404 locally.
  const [failed, setFailed] = useState(false);
  // A FILM that does not load leaves a black box with a play button on it — a control that cannot
  // act, over nothing. The box falls back to the picture, and then to the drawing, which is the
  // same ladder the still already walks.
  const [filmFailed, setFilmFailed] = useState(false);
  const still = stillUrl !== null && !failed ? stillUrl : null;
  const film_ = videoUrl !== null && !filmFailed ? videoUrl : null;

  return (
    <div
      className={styles.hero}
      {...(panel === undefined
        ? {}
        : { role: 'tabpanel', id: panel.id, 'aria-labelledby': panel.tabId })}
    >
      {film_ !== null ? (
        <video
          ref={film}
          className={styles.film}
          src={film_}
          onError={() => {
            setFilmFailed(true);
            setPlaying(false);
          }}
          poster={still ?? undefined}
          controls={playing}
          preload="metadata"
          // The film is the subject of the card and the bar above already names it, so the element
          // carries the movement's name rather than a second description of the same thing.
          aria-label={row.name}
        />
      ) : still !== null ? (
        <img
          className={`${String(photoBox.photo)} ${String(styles.still)}`}
          src={still}
          alt=""
          decoding="async"
          onError={() => {
            setFailed(true);
          }}
        />
      ) : (
        <span className={styles.tile}>
          <BarbellGlyph className={styles.tileGlyph} strokeWidth={1.2} />
          {mediaNote === null ? (
            <span className={styles.tileWord}>{t('web:exerciseLibrary.theatre.noCover')}</span>
          ) : (
            <span className={styles.tileNote} data-media-note>
              {mediaNote}
            </span>
          )}
        </span>
      )}
      {mediaNote !== null && (film_ !== null || still !== null) && (
        <span className={styles.plateNote} data-media-note>
          {mediaNote}
        </span>
      )}

      {/* The play button, on its own plate in the foot of the box. No scrim and no type mark here
        * any more (polish of 4 November 2026): the bar above prints the type and „Twoje", and a
        * gradient over the picture carried nothing else. `pointer-events: none` on the dock, so it
        * cannot swallow a press on the browser's control strip; the button takes its own back. */}
      {film_ !== null && !playing && (
        <div className={styles.playDock}>
          <button
            type="button"
            className={styles.play}
            aria-label={t('exerciseLibrary.detail.watchDemo')}
            onClick={() => {
              setPlaying(true);
              void film.current?.play();
            }}
          >
            <PlayGlyph className={styles.playGlyph} />
          </button>
        </div>
      )}
    </div>
  );
}
